// src/pages/TimesheetDetail.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { timesheetAPI, projectsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {
  Save, Send, CheckCircle, XCircle, Plus, Trash2, ChevronLeft, ChevronRight,
  AlertCircle, Info, History, Home, RotateCcw, Printer, FileSpreadsheet,
  Paperclip, Upload, Eye, X as XIcon, Lock,
} from 'lucide-react';
import './TimesheetDetail.css';

const MOIS_LABELS = ['Janvier','Février','Mars','Avril','Mai','Juin',
                     'Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
const JOURS_COURT = ['D','L','Ma','Me','J','V','S'];

const RH_ROLES = ['SUPER_ADMIN', 'ADMIN', 'RH'];

const CELL_VALUES = [0, 0.5, 1]; // clic cyclique (activite uniquement)

// TJM de la mission rattachée au projet de la ligne. L'API ne le renvoie qu'aux
// rôles autorisés (SUPER_ADMIN / RH / BUSINESS_MANAGER) — sinon il vaut null.
const fmtMoney = (v) =>
  new Intl.NumberFormat('fr-FR', {
    style: 'currency', currency: 'EUR',
    minimumFractionDigits: 0, maximumFractionDigits: 2,
  }).format(Number(v));

const renderTjm = (line) =>
  line.tjm === null || line.tjm === undefined ? null : (
    <span className="ts-tjm" title="TJM de la mission liée à ce projet">
      <span className="ts-tjm-label">TJM</span>
      <span className="ts-tjm-value">{fmtMoney(line.tjm)}</span>
    </span>
  );

// Couleurs par line_type + valeur
function getCellColor(lineType, valeur) {
  if (valeur === 0) return null;
  switch (lineType) {
    case 'activite':          return valeur === 0.5 ? '#4ADE80' : '#16A34A';
    case 'conge':             return '#F59E0B';
    case 'conge_maladie':     return '#FB923C';
    case 'conge_sans_solde':  return '#A78BFA';
    case 'rtt':               return '#D97706';
    case 'absence':           return '#EF4444';
    default:                  return '#16A34A';
  }
}

const LINE_TYPE_LABELS = {
  activite:          'Activité',
  teletravail:       'Télétravail',
  conge:             'Congés payés',
  conge_maladie:     'Congé maladie',
  conge_sans_solde:  'Congé sans solde',
  rtt:               'RTT',
  absence:           'Absence',
  ferie:             'Férié',
};

// Types absence ajoutables manuellement
const ABSENCE_TYPES = [
  { value: 'conge',            label: 'Congés payés' },
  { value: 'conge_maladie',    label: 'Congé maladie' },
  { value: 'conge_sans_solde', label: 'Congé sans solde' },
  { value: 'rtt',              label: 'RTT' },
  { value: 'absence',          label: 'Autre absence' },
];

function getDaysInMonth(annee, mois) {
  const days = [];
  const nb = new Date(annee, mois, 0).getDate();
  for (let d = 1; d <= nb; d++) {
    days.push(new Date(annee, mois - 1, d));
  }
  return days;
}

function isWeekend(d, pays) {
  const wd = d.getDay(); // 0=Dim, 1=Lun, ..., 5=Ven, 6=Sam
  // Algérie + Tunisie : week-end Vendredi(5) + Samedi(6)
  const p = (pays || '').toUpperCase();
  if (p === 'ALGERIE' || p === 'TUNISIE') return wd === 5 || wd === 6;
  // France, Belgique : Samedi(6) + Dimanche(0)
  return wd === 0 || wd === 6;
}

// Évite le bug timezone : toISOString() en UTC peut retourner le jour J-1
// pour les fuseaux horaires positifs (ex: France UTC+2 → minuit local = J-1 22h UTC)
function dayISO(d) {
  const y  = d.getFullYear();
  const m  = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

export default function TimesheetDetail() {
  const { annee: anneeStr, mois: moisStr } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const annee = parseInt(anneeStr, 10);
  const mois  = parseInt(moisStr,  10);
  const viewUserId = searchParams.get('user') || null;
  const isRH = RH_ROLES.includes(user?.role);
  const isViewOnly = isRH && viewUserId && viewUserId !== user?.id;

  const days = getDaysInMonth(annee, mois);

  const [ts,       setTs]       = useState(null);
  const [lines,    setLines]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [holidays, setHolidays] = useState(new Set());
  const [projects, setProjects] = useState([]);
  const [rejectOpen,      setRejectOpen]      = useState(false);
  const [rejectComment,   setRejectComment]   = useState('');
  const [absenceMenu,     setAbsenceMenu]     = useState(false);
  const [pj,              setPj]              = useState([]);
  const [pjLoading,       setPjLoading]       = useState(false);
  const [pjUploading,     setPjUploading]     = useState(false);
  const pjInputRef = useRef(null);
  const dirty = useRef(false);

  const pays = user?.site || 'FRANCE';

  const goMonth = (delta) => {
    let y = annee, m = mois + delta;
    if (m < 1)  { m = 12; y--; }
    if (m > 12) { m = 1;  y++; }
    navigate(`/timesheets/${y}/${m}${viewUserId ? `?user=${viewUserId}` : ''}`);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = viewUserId ? `?user=${viewUserId}` : '';
      const [tsRes, projRes] = await Promise.all([
        viewUserId
          ? timesheetAPI.list({ annee, mois, user_id: viewUserId })
          : timesheetAPI.getMine(annee, mois),
        viewUserId
          ? projectsAPI.list({ statut: 'EN_COURS' })
          : projectsAPI.list({ statut: 'EN_COURS', for_me: true }),
      ]);

      let tsData;
      if (viewUserId) {
        const list = tsRes.data || [];
        tsData = list[0] || null;
        if (!tsData) { toast.error('Feuille introuvable'); navigate('/timesheets'); return; }
      } else {
        tsData = tsRes.data;
      }

      setTs(tsData);
      setProjects(projRes.data || []);

      // Normalise les lignes — chaque ligne a un map jour→day
      const normalized = (tsData.lines || []).map(line => ({
        ...line,
        line_type: line.line_type || 'activite',
        daysMap: Object.fromEntries((line.days || []).map(d => [d.jour, d])),
      }));
      setLines(normalized);

      // Jours fériés
      try {
        const hRes = await timesheetAPI.getHolidays(pays, annee, mois);
        setHolidays(new Set(hRes.data?.holidays || []));
      } catch {}

    } catch (e) {
      toast.error('Erreur chargement feuille de temps');
    } finally {
      setLoading(false);
    }
  }, [annee, mois, viewUserId, pays, navigate]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Pièces jointes ────────────────────────────────────────────
  const loadPj = useCallback(async () => {
    if (!ts?.id) return;
    setPjLoading(true);
    try {
      const res = await timesheetAPI.listPJ(ts.id);
      setPj(res.data || []);
    } catch {} finally { setPjLoading(false); }
  }, [ts?.id]);

  useEffect(() => { loadPj(); }, [loadPj]);

  const handlePjUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const allowed = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error('Seuls PDF, JPG, PNG et WEBP sont acceptés');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 10 Mo)');
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    setPjUploading(true);
    try {
      await timesheetAPI.uploadPJ(ts.id, formData);
      toast.success('Pièce jointe ajoutée');
      loadPj();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur upload');
    } finally { setPjUploading(false); }
  };

  const handlePjDelete = async (pjId, fileName) => {
    if (!window.confirm(`Supprimer "${fileName}" ?`)) return;
    try {
      await timesheetAPI.deletePJ(ts.id, pjId);
      toast.success('Pièce jointe supprimée');
      loadPj();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur suppression');
    }
  };

  // ── Cellule click (cycle valeur) ──────────────────────────────
  const handleCellClick = (lineIdx, dayIso) => {
    if (!canEdit) return;
    dirty.current = true;
    setLines(prev => {
      const clickedLine = prev[lineIdx];
      const lt = clickedLine.line_type || 'activite';
      const lockedDays = ts?.locked_days_by_type || {};

      // Bloquer UNIQUEMENT la ligne elle-même si ce jour est verrouillé sur CETTE ligne
      const thisLineLockedSet = new Set(lockedDays[lt] || []);
      if (thisLineLockedSet.has(dayIso)) {
        toast('🔒 Ce jour est verrouillé par une absence validée', { icon: '🔒' });
        return prev;
      }

      const existing = clickedLine.daysMap[dayIso];
      const cur = existing?.valeur ?? 0;
      const isBinary = lt === 'teletravail';
      let nextVal;

      if (isBinary) {
        nextVal = cur === 0 ? 1 : 0;
      } else {
        // Total déjà pris par les AUTRES lignes non-télétravail ce jour (y compris les verrouillées)
        const otherTotal = prev.reduce((acc, line, i) => {
          if (i === lineIdx) return acc;
          if ((line.line_type || 'activite') === 'teletravail') return acc;
          return acc + (line.daysMap[dayIso]?.valeur ?? 0);
        }, 0);
        const maxAllowed = Math.round((1 - otherTotal) * 2) / 2; // 0, 0.5 ou 1
        const allowedValues = CELL_VALUES.filter(v => v <= maxAllowed + 0.001);
        if (allowedValues.length === 0 || maxAllowed <= 0) {
          toast('⚠️ Total journalier déjà à 1 jour', { icon: '⚠️' });
          return prev;
        }
        const curIdx = allowedValues.indexOf(cur);
        nextVal = allowedValues[(curIdx + 1) % allowedValues.length];
      }

      const typeMap = {
        activite:         nextVal === 0 ? 'absent' : 'present',
        teletravail:      nextVal === 0 ? 'absent' : 'teletravail',
        conge:            nextVal === 0 ? 'absent' : 'conge',
        conge_maladie:    nextVal === 0 ? 'absent' : 'conge',
        conge_sans_solde: nextVal === 0 ? 'absent' : 'conge',
        rtt:              nextVal === 0 ? 'absent' : 'rtt',
        absence:          nextVal === 0 ? 'absent' : 'absent',
      };
      const nextTypeJour = nextVal === 0.5 ? 'present' : (typeMap[lt] || 'present');
      const isAbsence = lt !== 'activite' && lt !== 'teletravail';

      return prev.map((line, i) => {
        if (i === lineIdx) {
          return {
            ...line,
            daysMap: {
              ...line.daysMap,
              [dayIso]: { ...(existing || { jour: dayIso }), valeur: nextVal, type_jour: nextTypeJour },
            },
          };
        }
        // Exclusivité journée complète (nextVal === 1) : vider les autres lignes
        // SAUF si cette autre ligne est verrouillée pour ce jour
        if (nextVal !== 1) return line;

        const otherLt = line.line_type || 'activite';
        const otherLockedSet = new Set(lockedDays[otherLt] || []);
        // Ne pas toucher une cellule verrouillée sur une autre ligne
        if (otherLockedSet.has(dayIso)) return line;

        const isOtherAbsence = otherLt !== 'activite' && otherLt !== 'teletravail';
        const otherCell = line.daysMap[dayIso];
        const clearCell = { ...(otherCell || { jour: dayIso }), valeur: 0, type_jour: otherLt === 'activite' ? 'absent' : otherLt };

        if (isAbsence && (otherLt === 'activite' || isOtherAbsence)) {
          return { ...line, daysMap: { ...line.daysMap, [dayIso]: clearCell } };
        }
        if (lt === 'activite' && isOtherAbsence) {
          return { ...line, daysMap: { ...line.daysMap, [dayIso]: clearCell } };
        }
        return line;
      });
    });
  };

  const _buildEmptyLine = (line_type, libelle) => {
    const newLine = {
      id: null, project_id: null,
      libelle, line_type,
      ordre: lines.length,
      daysMap: {},
    };
    days.forEach(d => {
      const iso = dayISO(d);
      const isFerie = holidays.has(iso);
      const isWE = isWeekend(d, pays);
      newLine.daysMap[iso] = {
        jour: iso, valeur: 0,
        type_jour: isFerie ? 'ferie' : isWE ? 'absent' : line_type === 'activite' ? 'present' : line_type,
      };
    });
    return newLine;
  };

  const addLine = () => {
    // Ajoute uniquement une ligne Activité (projet)
    if (!canEdit) return;
    dirty.current = true;
    setLines(prev => [...prev, _buildEmptyLine('activite', '')]);
  };

  const addAbsence = (type) => {
    if (!canEdit) return;
    if (lines.some(l => l.line_type === type)) {
      toast.error(`Une ligne "${LINE_TYPE_LABELS[type] || type}" existe déjà`);
      setAbsenceMenu(false);
      return;
    }
    dirty.current = true;
    const label = LINE_TYPE_LABELS[type] || type;
    setLines(prev => [...prev, _buildEmptyLine(type, label)]);
    setAbsenceMenu(false);
  };

  const removeLine = (idx) => {
    if (!canEdit) return;
    dirty.current = true;
    setLines(prev => prev.filter((_, i) => i !== idx));
  };

  const handleProjectChange = (lineIdx, projectId) => {
    if (!canEdit) return;
    if (projectId && lines.some((l, i) => i !== lineIdx && l.project_id === projectId)) {
      toast.error('Ce projet est déjà présent dans la feuille de temps');
      return;
    }
    dirty.current = true;
    const proj = projects.find(p => p.id === projectId);
    setLines(prev => prev.map((line, i) =>
      // tjm remis à null : il est recalculé par l'API au prochain enregistrement,
      // sinon on afficherait le TJM de l'ancien projet
      i === lineIdx ? { ...line, project_id: projectId || null, project_nom: proj?.nom || null, libelle: '', tjm: null } : line
    ));
  };

  const handleLabelChange = (lineIdx, val) => {
    if (!canEdit) return;
    dirty.current = true;
    setLines(prev => prev.map((line, i) =>
      i === lineIdx ? { ...line, libelle: val } : line
    ));
  };

  const handleLineTypeChange = (lineIdx, newType) => {
    if (!canEdit) return;
    dirty.current = true;
    setLines(prev => prev.map((line, i) => {
      if (i !== lineIdx) return line;
      // Mettre à jour aussi les type_jour dans daysMap
      const newDaysMap = {};
      Object.entries(line.daysMap).forEach(([iso, cell]) => {
        const isFerie = holidays.has(iso);
        const d = new Date(iso);
        const isWE = isWeekend(d, pays);
        const isOff = isFerie || isWE;
        let type_jour = cell.type_jour;
        if (!isOff) {
          const map = { activite: 'present', teletravail: 'teletravail', conge: 'conge', rtt: 'rtt', absence: 'absent' };
          type_jour = map[newType] || 'present';
        }
        newDaysMap[iso] = { ...cell, type_jour };
      });
      const libelle = LINE_TYPE_LABELS[newType] !== 'Activité' ? LINE_TYPE_LABELS[newType] : line.libelle;
      return { ...line, line_type: newType, daysMap: newDaysMap, libelle: newType !== 'activite' ? libelle : line.libelle };
    }));
  };

  // ── Build payload ──────────────────────────────────────────────
  const buildPayload = () => ({
    lines: lines.map((line, idx) => ({
      id:         line.id   || undefined,
      project_id: line.project_id || null,
      libelle:    line.libelle   || null,
      ordre:      idx,
      line_type:  line.line_type || 'activite',
      days: days.map(d => {
        const iso = dayISO(d);
        const cell = line.daysMap[iso];
        return {
          jour:      iso,
          valeur:    cell?.valeur    ?? 0,
          type_jour: cell?.type_jour ?? 'absent',
        };
      }),
    })),
  });

  const handleSave = async () => {
    if (!ts) return;
    setSaving(true);
    try {
      const res = await timesheetAPI.save(ts.id, buildPayload());
      const updated = res.data;
      setTs(updated);
      const normalized = (updated.lines || []).map(line => ({
        ...line,
        line_type: line.line_type || 'activite',
        daysMap: Object.fromEntries((line.days || []).map(d => [d.jour, d])),
      }));
      setLines(normalized);
      dirty.current = false;
      toast.success('Feuille sauvegardée');
    } catch { toast.error('Erreur sauvegarde'); }
    finally { setSaving(false); }
  };

  const handleSubmit = async () => {
    if (!ts) return;
    if (!window.confirm('Soumettre votre feuille de temps pour validation RH ?')) return;
    setSaving(true);
    try {
      if (dirty.current) await timesheetAPI.save(ts.id, buildPayload());
      await timesheetAPI.submit(ts.id);
      toast.success('Feuille soumise pour validation');
      loadData();
    } catch { toast.error('Erreur soumission'); }
    finally { setSaving(false); }
  };

  const handleValidate = async () => {
    if (!ts) return;
    setSaving(true);
    try {
      await timesheetAPI.validate(ts.id, {});
      toast.success('Feuille validée');
      loadData();
    } catch { toast.error('Erreur validation'); }
    finally { setSaving(false); }
  };

  const handleReject = async () => {
    if (!ts) return;
    setSaving(true);
    try {
      await timesheetAPI.reject(ts.id, { commentaire_rh: rejectComment });
      toast.success('Feuille rejetée');
      setRejectOpen(false);
      loadData();
    } catch { toast.error('Erreur rejet'); }
    finally { setSaving(false); }
  };

  const handleReset = async () => {
    if (!window.confirm('Réinitialiser la feuille de temps ? Toutes les saisies seront effacées.')) return;
    setSaving(true);
    try {
      const res = await timesheetAPI.reset(annee, mois);
      const updated = res.data;
      setTs(updated);
      const normalized = (updated.lines || []).map(line => ({
        ...line,
        line_type: line.line_type || 'activite',
        daysMap: Object.fromEntries((line.days || []).map(d => [d.jour, d])),
      }));
      setLines(normalized);
      dirty.current = false;
      toast.success('Feuille réinitialisée');
    } catch { toast.error('Erreur réinitialisation'); }
    finally { setSaving(false); }
  };

  const canEdit = ts && ['BROUILLON','REJETE'].includes(ts.statut) && !isViewOnly;

  if (loading) return <div className="loading-state"><div className="spinner"/></div>;
  if (!ts) return null;

  // Totaux par ligne
  const totalByLine = lines.map(line =>
    days.reduce((acc, d) => {
      const iso = dayISO(d);
      return acc + (line.daysMap[iso]?.valeur || 0);
    }, 0)
  );

  // Grand total travail : UNIQUEMENT les lignes activite (pour le récapitulatif "Travail: X j")
  const grandTotal = lines.reduce((acc, line, i) =>
    line.line_type === 'activite' ? acc + totalByLine[i] : acc, 0);

  // Total jours comptabilisés : activite + absences (sans télétravail) — pour la ligne Total du tableau
  const totalJours = lines.reduce((acc, line, i) =>
    line.line_type !== 'teletravail' ? acc + totalByLine[i] : acc, 0);

  // Totaux par type pour le récapitulatif footer
  const totalTT    = lines.reduce((acc, l, i) => l.line_type === 'teletravail' ? acc + totalByLine[i] : acc, 0);
  const totalConge = lines.reduce((acc, l, i) => l.line_type === 'conge'       ? acc + totalByLine[i] : acc, 0);
  const totalRtt   = lines.reduce((acc, l, i) => l.line_type === 'rtt'         ? acc + totalByLine[i] : acc, 0);
  const totalMaladie    = lines.reduce((acc, l, i) => l.line_type === 'conge_maladie'    ? acc + totalByLine[i] : acc, 0);
  const totalSansSolde  = lines.reduce((acc, l, i) => l.line_type === 'conge_sans_solde' ? acc + totalByLine[i] : acc, 0);

  // Helper : nom affiché d'une ligne activité
  // project_nom peut être null dans le state local si sélectionné via dropdown sans reload
  // → fallback sur le tableau projects chargé séparément
  const lineLabel = (line) => {
    if (line.project_nom) return line.project_nom;
    if (line.project_id) {
      const proj = projects.find(p => String(p.id) === String(line.project_id));
      if (proj) return proj.nom;
    }
    return line.libelle || null;
  };

  // Séparer les lignes par section
  const activiteLines = lines.map((l, i) => ({ line: l, idx: i })).filter(({ line }) =>
    line.line_type === 'activite' || line.line_type === 'teletravail'
  );
  const absenceLines  = lines.map((l, i) => ({ line: l, idx: i })).filter(({ line }) =>
    line.line_type !== 'activite' && line.line_type !== 'teletravail'
  );

  // ── Export Excel (CSV compatible Excel français) ───────────────
  const downloadExcel = () => {
    const collab = `${ts.user_prenom || ''} ${ts.user_nom || ''}`.trim() || 'Collaborateur';
    const moisLabel = MOIS_LABELS[mois - 1];

    const rows = [
      [`Feuille de temps — ${moisLabel} ${annee}`],
      [`Collaborateur : ${collab}`],
      [`Statut : ${ts.statut}`],
      [],
      ['Activité / Projet', 'Type', `Jours (${moisLabel} ${annee})`],
    ];

    activiteLines.forEach(({ line, idx }) => {
      const label = lineLabel(line) || 'Activité';
      rows.push([label, 'Activité', String(totalByLine[idx].toFixed(1)).replace('.', ',')]);
    });

    rows.push([]);
    rows.push(['TOTAL TRAVAIL', '', String(grandTotal.toFixed(1)).replace('.', ',')]);
    if (totalConge > 0)     rows.push(['Congés payés',    'CP',      String(totalConge.toFixed(1)).replace('.', ',')]);
    if (totalRtt > 0)       rows.push(['RTT',              'RTT',     String(totalRtt.toFixed(1)).replace('.', ',')]);
    if (totalMaladie > 0)   rows.push(['Congé maladie',   'Maladie', String(totalMaladie.toFixed(1)).replace('.', ',')]);
    if (totalSansSolde > 0) rows.push(['Congé sans solde','CSS',     String(totalSansSolde.toFixed(1)).replace('.', ',')]);
    if (totalTT > 0)        rows.push(['Télétravail',      'TT',      String(totalTT.toFixed(1)).replace('.', ',')]);

    // BOM UTF-8 + séparateur ; (standard Excel français)
    // Les valeurs numériques (colonne index 2) ne doivent PAS être quotées
    // sinon Excel français les interprète comme du texte et ignore les 0,5
    const isNumeric = (s) => /^-?\d+[,.]?\d*$/.test(String(s ?? ''));
    const csv = '﻿' + rows
      .map(r => r.map((c, ci) => {
        const s = String(c ?? '');
        // Colonne valeur (index 2 sur les lignes de données) : pas de quotes si numérique
        if (ci === 2 && isNumeric(s)) return s;
        return `"${s.replace(/"/g, '""')}"`;
      }).join(';'))
      .join('\r\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `fdt_${annee}_${String(mois).padStart(2, '0')}_${collab.replace(/\s+/g, '_')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const renderCell = (line, lineIdx, d) => {
    const iso   = dayISO(d);
    const cell  = line.daysMap[iso];
    const val   = cell?.valeur ?? 0;
    const wd    = d.getDay();
    const isFerie = holidays.has(iso);
    const isWE    = isWeekend(d, pays);
    const isOff   = isFerie || isWE;
    const lt    = line.line_type || 'activite';

    const lockedDays = ts?.locked_days_by_type || {};
    const isLocked   = !isOff && new Set(lockedDays[lt] || []).has(iso);

    return (
      <td
        key={iso}
        className={`ts-cell ${isWE ? 'ts-day--weekend' : ''} ${isFerie ? 'ts-day--ferie' : ''} ${isLocked ? 'ts-cell--locked' : ''}`}
      >
        <div
          className={`ts-cell-inner ${canEdit && !isOff && !isLocked ? 'ts-cell-inner--clickable' : ''}`}
          onClick={() => !isOff && handleCellClick(lineIdx, iso)}
          title={isOff ? (isFerie ? 'Férié' : 'Week-end') : isLocked ? '🔒 Verrouillé — absence validée' : `Clic pour changer (${LINE_TYPE_LABELS[lt] || lt})`}
        >
          {isOff ? (
            <span className="ts-cell-off">{isFerie ? 'F' : '—'}</span>
          ) : isLocked ? (
            <span className="ts-cell-locked" title="Verrouillé par absence validée">
              {val > 0 ? (
                <>
                  <span
                    className={`ts-cell-val ${val === 0.5 ? 'ts-cell-half' : 'ts-cell-full'}`}
                    style={{ background: getCellColor(lt, val) }}
                  >{val === 0.5 ? '½' : '1'}</span>
                  <Lock size={9} className="ts-lock-icon"/>
                </>
              ) : (
                <Lock size={11} className="ts-lock-icon ts-lock-icon--empty"/>
              )}
            </span>
          ) : val === 0 ? (
            <span className="ts-cell-zero">·</span>
          ) : lt === 'teletravail' ? (
            // Télétravail : icône maison, pas de chiffre
            <span className="ts-cell-tt" title="Télétravail"><Home size={13} color="#3B82F6"/></span>
          ) : (
            <span
              className={`ts-cell-val ${val === 0.5 ? 'ts-cell-half' : 'ts-cell-full'}`}
              style={{ background: getCellColor(lt, val) }}
              title={LINE_TYPE_LABELS[lt] || lt}
            >
              {val === 0.5 ? '½' : '1'}
            </span>
          )}
        </div>
      </td>
    );
  };

  const renderLineLabel = (line, lineIdx) => {
    const lt = line.line_type || 'activite';
    const isFixedType = lt !== 'activite';

    if (!canEdit) {
      return (
        <div className="ts-label-edit">
          <span className="ts-label-text">
            {line.project_nom || line.libelle || LINE_TYPE_LABELS[lt] || '—'}
          </span>
          {renderTjm(line)}
        </div>
      );
    }

    if (isFixedType) {
      const lockedDays = ts?.locked_days_by_type || {};
      const hasLockedDay = (lockedDays[lt] || []).length > 0;
      return (
        <div className="ts-label-edit">
          <span className={`ts-linetype-badge ts-linetype-${lt}`}>{LINE_TYPE_LABELS[lt] || lt}</span>
          {hasLockedDay && (
            <span className="ts-line-lock-badge" title="Certains jours sont verrouillés par une absence validée">
              <Lock size={11}/>
            </span>
          )}
        </div>
      );
    }

    // Activité : sélecteur projet + libellé libre
    return (
      <div className="ts-label-edit">
        <select
          className="ts-select-project"
          value={line.project_id || ''}
          onChange={e => handleProjectChange(lineIdx, e.target.value)}
        >
          <option value="">— Aucun projet —</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.nom}</option>)}
        </select>
        {!line.project_id && (
          <input
            className="ts-input-label"
            placeholder="Libellé libre (ex: Interne…)"
            value={line.libelle || ''}
            onChange={e => handleLabelChange(lineIdx, e.target.value)}
          />
        )}
        {renderTjm(line)}
      </div>
    );
  };

  return (
    <div className="ts-detail">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <div className="ts-month-nav">
            <button className="btn btn-ghost btn-sm" onClick={() => goMonth(-1)} title="Mois précédent">
              <ChevronLeft size={16}/>
            </button>
            <h1 style={{ margin: 0 }}>
              {MOIS_LABELS[mois - 1]} {annee}
              {isViewOnly && ts.user_prenom && (
                <span style={{ fontSize:'1rem', fontWeight:400, color:'var(--text-muted)', marginLeft:12 }}>
                  — {ts.user_prenom} {ts.user_nom}
                </span>
              )}
            </h1>
            <button className="btn btn-ghost btn-sm" onClick={() => goMonth(+1)} title="Mois suivant">
              <ChevronRight size={16}/>
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/timesheets/history')} title="Historique">
              <History size={15}/> <span style={{ fontSize:'0.8rem' }}>Historique</span>
            </button>
          </div>
          <p style={{ marginTop: 4 }}>Feuille de temps mensuelle</p>
        </div>
        <div className="page-header-actions">
          <TSStatutBadge statut={ts.statut}/>

          <button className="btn btn-ghost btn-sm" onClick={downloadExcel} title="Télécharger résumé Excel">
            <FileSpreadsheet size={14}/> Excel
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => window.print()} title="Télécharger / Imprimer PDF">
            <Printer size={14}/> PDF
          </button>

          {canEdit && (
            <>
              <button className="btn btn-ghost btn-sm" onClick={handleReset} disabled={saving}
                title="Effacer toutes les saisies et repartir de zéro" style={{ color:'var(--text-muted)' }}>
                <RotateCcw size={14}/> Réinitialiser
              </button>
              <button className="btn btn-secondary" onClick={handleSave} disabled={saving}>
                <Save size={15}/> Sauvegarder
              </button>
              <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
                <Send size={15}/> Soumettre
              </button>
            </>
          )}

          {isRH && ts.statut === 'SOUMIS' && (
            <>
              <button className="btn btn-danger" onClick={() => setRejectOpen(true)} disabled={saving}>
                <XCircle size={15}/> Rejeter
              </button>
              <button className="btn btn-success" onClick={handleValidate} disabled={saving}>
                <CheckCircle size={15}/> Valider
              </button>
            </>
          )}
        </div>
      </div>

      {/* Commentaire RH */}
      {ts.commentaire_rh && (
        <div className="ts-rh-comment">
          <Info size={15}/> <strong>Commentaire RH :</strong> {ts.commentaire_rh}
        </div>
      )}

      {/* Grille */}
      <div className="ts-grid-wrapper">
        <table className="ts-grid" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: 155 }}/>
            {days.map(d => <col key={d.toISOString()} style={{ width: 30 }}/>)}
            <col style={{ width: 48 }}/>
            {canEdit && <col style={{ width: 30 }}/>}
          </colgroup>
          <thead>
            <tr>
              <th className="ts-col-label">Activité / Projet</th>
              {days.map(d => {
                const iso = dayISO(d);
                const wd  = d.getDay();
                const isFerie = holidays.has(iso);
                const isWE    = isWeekend(d, pays);
                return (
                  <th key={iso}
                    className={`ts-col-day ${isWE ? 'ts-day--weekend' : ''} ${isFerie ? 'ts-day--ferie' : ''}`}
                    title={isFerie ? 'Jour férié' : undefined}
                  >
                    <span className="ts-day-name">{JOURS_COURT[wd]}</span>
                    <span className="ts-day-num">{d.getDate()}</span>
                  </th>
                );
              })}
              <th className="ts-col-total">Total</th>
              {canEdit && <th className="ts-col-actions"/>}
            </tr>
          </thead>
          <tbody>
            {/* Section Activité + Télétravail */}
            {activiteLines.map(({ line, idx: lineIdx }) => (
              <tr key={lineIdx} className={`ts-row ts-row--${line.line_type || 'activite'}`}>
                <td className="ts-cell-label">
                  {renderLineLabel(line, lineIdx)}
                </td>
                {days.map(d => renderCell(line, lineIdx, d))}
                <td className="ts-cell-total">
                  <span>{totalByLine[lineIdx].toFixed(1)}</span>
                </td>
                {canEdit && (
                  <td className="ts-cell-actions">
                    {line.line_type === 'activite' && (
                      <button className="ts-btn-remove" onClick={() => removeLine(lineIdx)} title="Supprimer la ligne">
                        <Trash2 size={13}/>
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}

            {/* Séparateur visuel */}
            {absenceLines.length > 0 && (
              <tr className="ts-row-separator">
                <td colSpan={days.length + 2 + (canEdit ? 1 : 0)}>
                  <span className="ts-separator-label">Absences</span>
                </td>
              </tr>
            )}

            {/* Section Absences */}
            {absenceLines.map(({ line, idx: lineIdx }) => (
              <tr key={lineIdx} className={`ts-row ts-row--${line.line_type || 'conge'}`}>
                <td className="ts-cell-label">
                  {renderLineLabel(line, lineIdx)}
                </td>
                {days.map(d => renderCell(line, lineIdx, d))}
                <td className="ts-cell-total">
                  <span>{totalByLine[lineIdx].toFixed(1)}</span>
                </td>
                {canEdit && (
                  <td className="ts-cell-actions">
                    <button className="ts-btn-remove" onClick={() => removeLine(lineIdx)} title="Supprimer la ligne">
                      <Trash2 size={13}/>
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="ts-row-total">
              <td className="ts-cell-label"><strong>Total</strong></td>
              {days.map(d => {
                const iso = dayISO(d);
                // Activité + absences (teletravail exclu — c'est un marqueur de lieu)
                const dayTotal = lines.reduce((acc, line) => {
                  if (line.line_type === 'teletravail') return acc;
                  return acc + (line.daysMap[iso]?.valeur || 0);
                }, 0);
                const wd = d.getDay();
                const isFerie = holidays.has(iso);
                const isWE    = isWeekend(d, pays);
                return (
                  <td key={iso} className={`ts-cell ts-foot-cell ${isWE ? 'ts-day--weekend' : ''} ${isFerie ? 'ts-day--ferie' : ''}`}>
                    {dayTotal > 0 ? <strong>{dayTotal % 1 === 0 ? dayTotal : dayTotal.toFixed(1)}</strong> : ''}
                  </td>
                );
              })}
              <td className="ts-cell-total"><strong>{totalJours.toFixed(1)}</strong></td>
              {canEdit && <td/>}
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Footer : légende + récapitulatif + bouton ajout */}
      <div className="ts-footer">
        <div className="ts-legend">
          <span className="ts-legend-item"><span className="ts-legend-dot" style={{ background:'#16A34A' }}/>Présence</span>
          <span className="ts-legend-item"><Home size={14} color="#3B82F6"/> Télétravail</span>
          <span className="ts-legend-item"><span className="ts-legend-dot" style={{ background:'#F59E0B' }}/>CP</span>
          <span className="ts-legend-item"><span className="ts-legend-dot" style={{ background:'#FB923C' }}/>Maladie</span>
          <span className="ts-legend-item"><span className="ts-legend-dot" style={{ background:'#A78BFA' }}/>Sans solde</span>
          <span className="ts-legend-item"><span className="ts-legend-dot" style={{ background:'#D97706' }}/>RTT</span>
          <span className="ts-legend-item"><span className="ts-legend-dot" style={{ background:'#EF4444' }}/>Absence</span>
          <span className="ts-legend-item"><span className="ts-legend-dot" style={{ background:'var(--bg-elevated)', border:'1px solid var(--border)' }}>½</span>Demi-j.</span>
        </div>
        <div className="ts-recap">
          <span>Travail: <strong>{grandTotal.toFixed(1)} j</strong></span>
          {totalTT > 0          && <span style={{ display:'flex', alignItems:'center', gap:3 }}><Home size={12} color="#3B82F6"/> TT: <strong>{totalTT.toFixed(1)} j</strong></span>}
          {totalConge > 0       && <span>CP: <strong>{totalConge.toFixed(1)} j</strong></span>}
          {totalMaladie > 0     && <span>Maladie: <strong>{totalMaladie.toFixed(1)} j</strong></span>}
          {totalSansSolde > 0   && <span>CSS: <strong>{totalSansSolde.toFixed(1)} j</strong></span>}
          {totalRtt > 0         && <span>RTT: <strong>{totalRtt.toFixed(1)} j</strong></span>}
        </div>
        {canEdit && (
          <div style={{ display:'flex', gap:8, position:'relative' }}>
            <button className="btn btn-secondary btn-sm" onClick={addLine}>
              <Plus size={14}/> Projet
            </button>
            <div style={{ position:'relative' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setAbsenceMenu(v => !v)}>
                <Plus size={14}/> Absence
              </button>
              {absenceMenu && (
                <div style={{
                  position:'absolute', bottom:'110%', right:0, zIndex:200,
                  background:'var(--bg-card)', border:'1px solid var(--border)',
                  borderRadius:10, boxShadow:'var(--shadow-md)', minWidth:180, overflow:'hidden',
                }}>
                  {ABSENCE_TYPES.map(t => (
                    <button key={t.value} onClick={() => addAbsence(t.value)} style={{
                      display:'block', width:'100%', textAlign:'left',
                      padding:'9px 14px', background:'none', border:'none',
                      color:'var(--text)', fontSize:'0.8125rem', cursor:'pointer',
                    }}
                    onMouseEnter={e => e.target.style.background = 'var(--bg-elevated)'}
                    onMouseLeave={e => e.target.style.background = 'none'}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Résumé ────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>

        {/* Missions */}
        <div className="card" style={{ padding: '16px 20px' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase',
                        letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: 12 }}>
            Résumé missions et inter-contrats
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                <th style={{ textAlign:'left',  padding:'0 0 6px', fontWeight:600, color:'var(--text-muted)', fontSize:'0.75rem' }}>Projet / Mission</th>
                <th style={{ textAlign:'right', padding:'0 0 6px', fontWeight:600, color:'var(--text-muted)', fontSize:'0.75rem' }}>Jours</th>
              </tr>
            </thead>
            <tbody>
              {activiteLines
                .filter(({ line }) => line.line_type === 'activite')
                .map(({ line, idx }) => (
                  <tr key={idx} style={{ borderBottom: '1px solid var(--border-soft, rgba(255,255,255,0.04))' }}>
                    <td style={{ padding: '7px 0', color: '#3B82F6', fontWeight: 500 }}>
                      {lineLabel(line) || <span style={{ color:'var(--text-faint)', fontWeight:400 }}>— Sans projet —</span>}
                    </td>
                    <td style={{ padding: '7px 0', textAlign: 'right', fontWeight: 600 }}>
                      {totalByLine[idx].toFixed(1)} j
                    </td>
                  </tr>
                ))
              }
            </tbody>
            <tfoot>
              <tr style={{ borderTop: '2px solid var(--border)' }}>
                <td style={{ padding: '8px 0 0', fontWeight: 700 }}>Total travail</td>
                <td style={{ padding: '8px 0 0', textAlign: 'right', fontWeight: 700, color: '#3B82F6' }}>
                  {grandTotal.toFixed(1)} j
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Absences */}
        <div className="card" style={{ padding: '16px 20px' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase',
                        letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: 12 }}>
            Résumé absences
          </div>
          {absenceLines.every(({ idx }) => totalByLine[idx] === 0) ? (
            <p style={{ fontSize:'0.8125rem', color:'var(--text-faint)', margin:0 }}>Aucune absence ce mois</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  <th style={{ textAlign:'left',  padding:'0 0 6px', fontWeight:600, color:'var(--text-muted)', fontSize:'0.75rem' }}>Type d'absence</th>
                  <th style={{ textAlign:'right', padding:'0 0 6px', fontWeight:600, color:'var(--text-muted)', fontSize:'0.75rem' }}>Jours</th>
                </tr>
              </thead>
              <tbody>
                {absenceLines
                  .filter(({ idx }) => totalByLine[idx] > 0)
                  .map(({ line, idx }) => (
                    <tr key={idx} style={{ borderBottom: '1px solid var(--border-soft, rgba(255,255,255,0.04))' }}>
                      <td style={{ padding: '7px 0' }}>
                        <span style={{ display:'inline-flex', alignItems:'center', gap:8 }}>
                          <span style={{ width:8, height:8, borderRadius:'50%', flexShrink:0, display:'inline-block',
                                         background: getCellColor(line.line_type, 1) || '#888' }}/>
                          {LINE_TYPE_LABELS[line.line_type] || line.line_type}
                        </span>
                      </td>
                      <td style={{ padding: '7px 0', textAlign: 'right', fontWeight: 600 }}>
                        {totalByLine[idx].toFixed(1)} j
                      </td>
                    </tr>
                  ))
                }
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid var(--border)' }}>
                  <td style={{ padding: '8px 0 0', fontWeight: 700 }}>Total absences</td>
                  <td style={{ padding: '8px 0 0', textAlign: 'right', fontWeight: 700 }}>
                    {absenceLines.reduce((s, { idx }) => s + totalByLine[idx], 0).toFixed(1)} j
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>

      {/* ── Pièces jointes ───────────────────────────────────────── */}
      {(() => {
        const canUpload = canEdit;
        const canDeletePJ = canEdit || isRH;
        const fmtSize = (b) => b > 1048576 ? `${(b/1048576).toFixed(1)} Mo` : b > 1024 ? `${(b/1024).toFixed(0)} Ko` : `${b} o`;
        const fmtTs = (iso) => {
          if (!iso) return '';
          const d = new Date(iso);
          return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
        };
        return (
          <div className="card" style={{ padding: '18px 22px', marginTop: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Paperclip size={16} style={{ color: 'var(--text-muted)' }} />
                <span style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase',
                               letterSpacing: '0.08em', color: 'var(--text-muted)' }}>
                  Pièces jointes
                </span>
                {pj.length > 0 && (
                  <span style={{ whiteSpace: 'nowrap', fontSize: '0.72rem', fontWeight: 600, padding: '1px 7px', borderRadius: 20,
                                 background: 'var(--bg-elevated)', color: 'var(--text-muted)' }}>
                    {pj.length}
                  </span>
                )}
              </div>
              {canUpload && (
                <>
                  <input ref={pjInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp"
                         style={{ display: 'none' }} onChange={handlePjUpload} />
                  <button
                    onClick={() => pjInputRef.current?.click()}
                    disabled={pjUploading}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px',
                             borderRadius: 8, border: '1.5px dashed var(--border)',
                             background: 'transparent', cursor: pjUploading ? 'wait' : 'pointer',
                             color: 'var(--text-secondary)', fontSize: '0.8rem', fontWeight: 600 }}>
                    <Upload size={13} />
                    {pjUploading ? 'Envoi…' : 'Ajouter un fichier'}
                  </button>
                </>
              )}
            </div>

            {pjLoading ? (
              <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>Chargement…</div>
            ) : pj.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-muted)', fontSize: '0.8125rem' }}>
                {canUpload
                  ? 'Aucune pièce jointe — vous pouvez joindre un pointage client (PDF ou image)'
                  : 'Aucune pièce jointe'}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {pj.map(p => {
                  const isPdf = (p.file_mime || '').includes('pdf');
                  return (
                    <div key={p.id}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px',
                               borderRadius: 9, border: '1px solid var(--border)',
                               background: 'var(--bg-elevated)' }}>
                      <Paperclip size={13} style={{ color: isPdf ? '#DC2626' : '#0891B2', flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-primary)',
                                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {p.file_name}
                        </div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 1 }}>
                          Ajouté le {fmtTs(p.uploaded_at)}
                          {p.uploaded_by_prenom && ` par ${p.uploaded_by_prenom} ${p.uploaded_by_nom || ''}`}
                        </div>
                      </div>
                      {p.file_url && (
                        <a href={p.file_url} target="_blank" rel="noreferrer"
                           title="Voir / télécharger"
                           style={{ width: 28, height: 28, borderRadius: 7,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    border: '1px solid var(--border)', color: '#0891B2', background: 'transparent',
                                    textDecoration: 'none', flexShrink: 0 }}>
                          <Eye size={13} />
                        </a>
                      )}
                      {canDeletePJ && (
                        <button onClick={() => handlePjDelete(p.id, p.file_name)}
                          title="Supprimer"
                          style={{ width: 28, height: 28, borderRadius: 7, border: '1px solid rgba(220,38,38,0.3)',
                                   background: 'rgba(220,38,38,0.07)', color: '#DC2626',
                                   cursor: 'pointer', display: 'flex', alignItems: 'center',
                                   justifyContent: 'center', flexShrink: 0 }}>
                          <XIcon size={13} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {!canUpload && ts?.statut === 'SOUMIS' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10,
                            fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                <AlertCircle size={12} />
                La feuille est soumise — consultation uniquement
              </div>
            )}
          </div>
        );
      })()}

      {/* Vue impression PDF — masquée à l'écran, visible uniquement @media print */}
      <div className="ts-print-only">
        <div className="ts-print-title">
          Feuille de temps — {MOIS_LABELS[mois - 1]} {annee}
        </div>
        <div className="ts-print-meta">
          {ts.user_prenom} {ts.user_nom} · Statut : {ts.statut} · Travail : {grandTotal.toFixed(1)} j
          {totalConge > 0 && ` · CP : ${totalConge.toFixed(1)} j`}
          {totalRtt   > 0 && ` · RTT : ${totalRtt.toFixed(1)} j`}
        </div>
        <table className="ts-print-table">
          <thead>
            <tr>
              <th>Activité / Projet</th>
              {days.map(d => {
                const iso = dayISO(d);
                const wd  = d.getDay();
                const isWE = isWeekend(d, pays);
                const isFerie = holidays.has(iso);
                return (
                  <th key={iso} className={isWE || isFerie ? (isFerie ? 'ts-print-ferie' : 'ts-print-off') : ''}>
                    <div style={{ fontSize:'6pt', color:'#777' }}>{['D','L','Ma','Me','J','V','S'][wd]}</div>
                    {d.getDate()}
                  </th>
                );
              })}
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {activiteLines.map(({ line, idx }) => (
              <tr key={idx}>
                <td style={{ textAlign:'left' }}>
                  {lineLabel(line) || 'Activité'}
                </td>
                {days.map(d => {
                  const iso = dayISO(d);
                  const val = line.daysMap[iso]?.valeur ?? 0;
                  const isOff = isWeekend(d, pays) || holidays.has(iso);
                  return (
                    <td key={iso} className={isOff ? 'ts-print-off' : ''}>
                      {isOff ? '' : val === 0 ? '' : val === 0.5 ? '½' : '1'}
                    </td>
                  );
                })}
                <td><strong>{totalByLine[idx].toFixed(1)}</strong></td>
              </tr>
            ))}
            {absenceLines.length > 0 && (
              <tr className="ts-print-sep">
                <td colSpan={days.length + 2}>ABSENCES</td>
              </tr>
            )}
            {absenceLines.map(({ line, idx }) => (
              <tr key={idx}>
                <td style={{ textAlign:'left' }}>
                  {LINE_TYPE_LABELS[line.line_type] || line.libelle || line.line_type}
                </td>
                {days.map(d => {
                  const iso = dayISO(d);
                  const val = line.daysMap[iso]?.valeur ?? 0;
                  const isOff = isWeekend(d, pays) || holidays.has(iso);
                  return (
                    <td key={iso} className={isOff ? 'ts-print-off' : ''}>
                      {isOff ? '' : val === 0 ? '' : val === 0.5 ? '½' : '1'}
                    </td>
                  );
                })}
                <td><strong>{totalByLine[idx].toFixed(1)}</strong></td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="ts-print-total-row">
              <td style={{ textAlign:'left' }}>Total travail</td>
              {days.map(d => {
                const iso = dayISO(d);
                const t = lines.reduce((acc, l) =>
                  l.line_type === 'activite' ? acc + (l.daysMap[iso]?.valeur || 0) : acc, 0);
                const isOff = isWeekend(d, pays) || holidays.has(iso);
                return <td key={iso} className={isOff ? 'ts-print-off' : ''}>{isOff ? '' : t > 0 ? t : ''}</td>;
              })}
              <td><strong>{grandTotal.toFixed(1)}</strong></td>
            </tr>
          </tfoot>
        </table>
        <div className="ts-print-footer">
          Généré le {new Date().toLocaleDateString('fr-FR')} · WEBusiness by Wikeys
        </div>
      </div>

      {/* Modal rejet */}
      {rejectOpen && (
        <div className="modal-overlay" onClick={() => setRejectOpen(false)}>
          <div className="modal-card" onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom: 12 }}>Rejeter la feuille de temps</h3>
            <textarea
              className="form-control"
              rows={4}
              placeholder="Commentaire obligatoire pour le rejet…"
              value={rejectComment}
              onChange={e => setRejectComment(e.target.value)}
              style={{ width:'100%', resize:'vertical' }}
            />
            <div style={{ display:'flex', gap:10, marginTop:16, justifyContent:'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setRejectOpen(false)}>Annuler</button>
              <button className="btn btn-danger" onClick={handleReject} disabled={!rejectComment.trim() || saving}>
                <XCircle size={14}/> Confirmer le rejet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TSStatutBadge({ statut }) {
  const META = {
    BROUILLON: { label: 'Brouillon',  color: '#8B8FA8', bg: 'rgba(139,143,168,0.12)' },
    SOUMIS:    { label: 'Soumis',     color: '#F59E0B', bg: 'rgba(245,158,11,0.12)' },
    VALIDE:    { label: 'Validé',     color: '#16A34A', bg: 'rgba(34,197,94,0.10)' },
    REJETE:    { label: 'Rejeté',     color: '#DC2626', bg: 'rgba(239,68,68,0.12)' },
  };
  const m = META[statut] || META.BROUILLON;
  return (
    <span className="badge" style={{ color: m.color, background: m.bg, border: `1px solid ${m.color}33`, fontSize:'0.8125rem', padding:'5px 12px' }}>
      {m.label}
    </span>
  );
}
